import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.png';

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const token = searchParams.get('token') || '';

  // Schritt 1: Token prüfen
  const [checking, setChecking] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [tokenError, setTokenError] = useState('');

  // Formular
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenError('Kein Einladungstoken in der URL – schau noch mal in der E-Mail nach dem Link.');
      setChecking(false);
      return;
    }
    authAPI
      .inviteVerify(token)
      .then((res) => {
        setTokenValid(true);
        setEmail(res.data.email);
      })
      .catch((err) => {
        const msg =
          err.response?.data?.error ||
          'Der Einladungslink ist ungültig oder abgelaufen.';
        setTokenError(msg);
      })
      .finally(() => setChecking(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (password !== passwordConfirm) {
      setFormError('Die Passwörter stimmen nicht überein.');
      return;
    }
    if (password.length < 8) {
      setFormError('Das Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }

    setSubmitting(true);
    try {
      await authAPI.inviteAccept(token, password, firstName, lastName);
      setDone(true);
      // Kurz warten, dann weiterleiten
      setTimeout(() => navigate('/', { replace: true }), 1800);
    } catch (err) {
      const data = err.response?.data?.error;
      const msg = Array.isArray(data) ? data.join(' ') : data || 'Fehler beim Erstellen des Kontos.';
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Zustände ──────────────────────────────────────────────────────────────

  const shell = (content) => (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f1f5f9' }}>
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-8 pt-8 pb-6 border-b border-gray-100 flex flex-col items-center gap-4">
            <div className="bg-hrf-navy rounded-xl px-4 py-2.5">
              <img src={logo} alt="HR Factory" className="h-8 w-auto" />
            </div>
          </div>
          {content}
        </div>
      </div>
    </div>
  );

  if (checking) {
    return shell(
      <div className="px-8 py-10 text-center">
        <p className="text-sm text-slate-500">Einladung wird geprüft…</p>
      </div>
    );
  }

  if (!tokenValid) {
    return shell(
      <div className="px-8 py-10 text-center space-y-3">
        <p className="text-base font-semibold text-red-700">Ungültiger Link</p>
        <p className="text-sm text-slate-500">{tokenError}</p>
        <p className="text-xs text-slate-400 mt-4">
          Schreib deinem Admin – der kann dir gern eine neue Einladung schicken.
        </p>
      </div>
    );
  }

  if (done) {
    return shell(
      <div className="px-8 py-10 text-center space-y-3">
        <div className="mx-auto w-12 h-12 rounded-full bg-hrf-coral/10 flex items-center justify-center">
          <svg className="h-6 w-6 text-hrf-coral" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-base font-semibold text-slate-800">Konto erstellt!</p>
        <p className="text-sm text-slate-500">Du wirst gleich weitergeleitet …</p>
      </div>
    );
  }

  return shell(
    <form onSubmit={handleSubmit} className="px-8 py-7 space-y-5">
      <div className="text-center">
        <h1 className="text-xl font-semibold text-slate-800">Konto einrichten</h1>
        <p className="text-sm text-slate-500 mt-1">{email}</p>
      </div>

      {formError && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {formError}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-slate-700" htmlFor="first_name">
            Vorname
          </label>
          <input
            id="first_name"
            type="text"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Anna"
            className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm text-slate-900
                       placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-hrf-navy
                       focus:border-transparent transition"
          />
        </div>
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-slate-700" htmlFor="last_name">
            Nachname
          </label>
          <input
            id="last_name"
            type="text"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Müller"
            className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm text-slate-900
                       placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-hrf-navy
                       focus:border-transparent transition"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-slate-700" htmlFor="password">
          Passwort wählen
        </label>
        <input
          id="password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Mindestens 8 Zeichen"
          className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm text-slate-900
                     placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-hrf-navy
                     focus:border-transparent transition"
        />
      </div>

      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-slate-700" htmlFor="password_confirm">
          Passwort bestätigen
        </label>
        <input
          id="password_confirm"
          type="password"
          autoComplete="new-password"
          required
          value={passwordConfirm}
          onChange={(e) => setPasswordConfirm(e.target.value)}
          placeholder="••••••••"
          className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm text-slate-900
                     placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-hrf-navy
                     focus:border-transparent transition"
        />
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-hrf-navy hover:bg-hrf-navy-800 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm py-2.5 rounded-lg transition-colors duration-150"
      >
        {submitting ? 'Konto wird erstellt…' : 'Konto erstellen'}
      </button>

      <p className="text-xs text-center text-slate-400">
        Mit dem Klick erklärst du dich mit der Nutzung im beruflichen Kontext einverstanden.
      </p>
    </form>
  );
}
